import { loadConfig, type RuntimeConfig } from './config.js';

/**
 * Authentification par **code d'autorisation + PKCE** contre le managed login
 * Cognito.
 *
 * Pourquoi PKCE et pas le flot implicite : le flot implicite rend les jetons
 * dans le fragment de l'URL, donc dans l'historique du navigateur et dans tout
 * `Referer` mal configuré. PKCE échange un code à usage unique contre les
 * jetons, par un POST que seul le détenteur du `code_verifier` peut faire — et
 * ce client étant **public**, il n'a aucun secret à protéger, ce qui est
 * exactement le cas d'usage de PKCE.
 *
 * **Où vivent les jetons.** En `localStorage`, limite assumée et écrite (D37) :
 * un jeton « en mémoire » reste lisible par du JavaScript injecté dans la même
 * origine, si bien que la défense de premier rang est l'absence de XSS — CSP
 * stricte, aucun octet utilisateur sur cette origine (I-20). Le jeton d'admin,
 * lui, n'y sera jamais (D21).
 */

const STORAGE_KEY = 'synkara.session';
const VERIFIER_KEY = 'synkara.pkce.verifier';
const STATE_KEY = 'synkara.pkce.state';
const RETURN_KEY = 'synkara.pkce.returnTo';

/** Les scopes demandés. `openid` est obligatoire pour obtenir un ID token. */
const SCOPES = ['openid', 'email', 'profile'];

export interface Session {
  readonly accessToken: string;
  readonly idToken: string;
  readonly refreshToken?: string;
  /** Instant d'expiration en millisecondes epoch. */
  readonly expiresAt: number;
}

/** Marge avant expiration : on rafraîchit sans attendre le dernier moment. */
const REFRESH_MARGIN_MS = 60_000;

// ─────────────────────────────────────────────────────────────────────────────
// PKCE
// ─────────────────────────────────────────────────────────────────────────────

function base64url(bytes: ArrayBuffer): string {
  const s = String.fromCharCode(...new Uint8Array(bytes));
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomUrlSafe(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64url(bytes.buffer);
}

async function challengeOf(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64url(digest);
}

function redirectUri(): string {
  // Déduit de l'origine, jamais codé en dur : le même bundle sert le serveur de
  // développement et le site déployé, dont les URL de rappel sont toutes deux
  // déclarées sur l'app client.
  return `${location.origin}/callback`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Stockage
// ─────────────────────────────────────────────────────────────────────────────

export function storedSession(): Session | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const s = JSON.parse(raw) as Session;
    if (typeof s.accessToken !== 'string' || typeof s.expiresAt !== 'number') return null;
    return s;
  } catch {
    // Un stockage corrompu ne doit pas bloquer l'utilisateur sur une page morte.
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

function store(s: Session): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
}

export function clearSession(): void {
  localStorage.removeItem(STORAGE_KEY);
}

// ─────────────────────────────────────────────────────────────────────────────
// Parcours
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Redirige vers le managed login. Ne rend jamais la main.
 *
 * `prompt: 'login'` **exige une ré-authentification même en présence d'une
 * session** — Cognito le documente explicitement sur l'endpoint d'autorisation.
 * C'est ce qui permet à la console d'administration de tenir D21 sans le client
 * `admin`, dont l'OAuth est désactivé et qui aurait donc imposé une page de mot
 * de passe maison : demander soi-même un mot de passe est précisément ce qu'un
 * client public ne doit jamais faire.
 */
export async function beginLogin(
  opts: { readonly prompt?: 'login'; readonly returnTo?: string } = {},
): Promise<never> {
  const cfg = await loadConfig();
  const verifier = randomUrlSafe(48);
  const state = randomUrlSafe(16);

  // `sessionStorage` et pas `localStorage` : ces deux valeurs ne servent qu'à
  // l'aller-retour en cours et n'ont aucune raison de survivre à l'onglet.
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);
  if (opts.returnTo) sessionStorage.setItem(RETURN_KEY, opts.returnTo);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: cfg.appClientId,
    redirect_uri: redirectUri(),
    scope: SCOPES.join(' '),
    state,
    code_challenge: await challengeOf(verifier),
    code_challenge_method: 'S256',
    ...(opts.prompt ? { prompt: opts.prompt } : {}),
  });

  location.assign(`https://${cfg.authDomain}/oauth2/authorize?${params}`);
  return new Promise<never>(() => {});
}

/** L'écran à rouvrir au retour d'une ré-authentification, consommé une seule fois. */
export function takeReturnTo(): string | null {
  const v = sessionStorage.getItem(RETURN_KEY);
  sessionStorage.removeItem(RETURN_KEY);
  return v;
}

/**
 * **Depuis combien de temps la personne s'est-elle authentifiée ?**
 *
 * `auth_time` date l'**authentification**, pas l'émission du jeton : un
 * rafraîchissement le conserve, ce qui est exactement ce qui le rend utile. On
 * le lit ici sans vérifier la signature — c'est légitime parce que la valeur ne
 * sert **qu'à éviter un aller-retour inutile** : l'autorité reste le serveur,
 * qui relit le même claim sur un jeton dont l'autorisateur a, lui, vérifié la
 * signature. Un jeton trafiqué localement n'obtiendrait rien de plus qu'un `401`.
 */
export function authAgeSeconds(session: Session): number | null {
  const part = session.idToken.split('.')[1];
  if (!part) return null;
  try {
    const payload = JSON.parse(
      atob(part.replace(/-/g, '+').replace(/_/g, '/')),
    ) as Record<string, unknown>;
    const authTime = Number(payload['auth_time']);
    if (!Number.isFinite(authTime)) return null;
    return Math.floor(Date.now() / 1000) - authTime;
  } catch {
    return null;
  }
}

/**
 * Termine le parcours au retour sur `/callback`, et rend la session.
 *
 * Le `state` est vérifié avant tout échange : sans lui, un tiers pourrait faire
 * aboutir *son* code d'autorisation dans *votre* navigateur.
 */
export async function completeLogin(search: string): Promise<Session> {
  const cfg = await loadConfig();
  const params = new URLSearchParams(search);

  const error = params.get('error');
  if (error) {
    throw new Error(`${error} — ${params.get('error_description') ?? 'sans détail'}`);
  }

  const code = params.get('code');
  const state = params.get('state');
  const expectedState = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);

  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);

  if (!code) throw new Error('retour sans code d’autorisation');
  if (!verifier) throw new Error('aucun code_verifier — le parcours n’a pas commencé ici');
  if (!state || state !== expectedState) throw new Error('state invalide — parcours rejeté');

  const session = await exchange(cfg, {
    grant_type: 'authorization_code',
    client_id: cfg.appClientId,
    code,
    redirect_uri: redirectUri(),
    code_verifier: verifier,
  });

  store(session);
  return session;
}

/**
 * Une session utilisable, ou `null`. Rafraîchit de façon transparente si le
 * jeton expire bientôt et qu'un `refresh_token` est disponible.
 */
export async function currentSession(): Promise<Session | null> {
  const s = storedSession();
  if (!s) return null;
  if (s.expiresAt - Date.now() > REFRESH_MARGIN_MS) return s;

  if (!s.refreshToken) {
    clearSession();
    return null;
  }

  const cfg = await loadConfig();
  try {
    const refreshed = await exchange(cfg, {
      grant_type: 'refresh_token',
      client_id: cfg.appClientId,
      refresh_token: s.refreshToken,
    });
    // Cognito ne renvoie pas de nouveau `refresh_token` : on conserve l'ancien.
    const merged: Session = { ...refreshed, refreshToken: s.refreshToken };
    store(merged);
    return merged;
  } catch {
    // Jeton de rafraîchissement révoqué ou expiré : on repart d'une page propre
    // plutôt que de boucler sur des 401.
    clearSession();
    return null;
  }
}

/** Déconnexion : session locale détruite, puis session Cognito. */
export async function logout(): Promise<never> {
  const cfg = await loadConfig();
  clearSession();
  const params = new URLSearchParams({
    client_id: cfg.appClientId,
    logout_uri: `${location.origin}/`,
  });
  location.assign(`https://${cfg.authDomain}/logout?${params}`);
  return new Promise<never>(() => {});
}

async function exchange(
  cfg: RuntimeConfig,
  body: Record<string, string>,
): Promise<Session> {
  const res = await fetch(`https://${cfg.authDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams(body),
  });

  const payload = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    throw new Error(`échange de jeton refusé (${res.status}) : ${payload['error'] ?? '?'}`);
  }

  const accessToken = payload['access_token'];
  const idToken = payload['id_token'];
  if (typeof accessToken !== 'string' || typeof idToken !== 'string') {
    throw new Error('réponse de jeton incomplète');
  }

  const expiresIn = typeof payload['expires_in'] === 'number' ? payload['expires_in'] : 3600;
  const refreshToken =
    typeof payload['refresh_token'] === 'string' ? payload['refresh_token'] : undefined;

  return { accessToken, idToken, refreshToken, expiresAt: Date.now() + expiresIn * 1000 };
}
